import { Button } from "@/src/components/buttons";
import { closeDialog, openDialog, openDrawer } from "@/src/components/dialogs";
import { Patient } from "@/src/data/allTypes";
import apiFetch from "@/src/utils/apiFetch";
import { openToast } from "@/src/utils/toasts";
import { getServiceId, getUserId } from "@/src/utils/utils";
import { useEffect, useRef, useState } from "react";
import Webcam from "react-webcam";
import Tesseract from "tesseract.js";
import { PatientIdentityForm } from "./patientIdentityForm";

export function openCardIdReaderDialog({
  onDetect,
  onSelect,
}: {
  onDetect?: (nin: string) => void;
  onSelect?: (patient: Patient, alreadyExists: boolean) => void;
}) {
  return openDialog({
    id: "cardIdReaderDialog",
    title: "Scanner une carte d'identité ou un code barre",
    content: (
      <CardIdReader
        onDetect={async (nin) => {
          try {
            await onDetect?.(nin);
            const { patient, alreadyExists } = await apiFetch(
              "patients",
              "loadByNin",
              {
                nin,
                userId: getUserId(),
                serviceId: getServiceId(),
              }
            );

            if (alreadyExists) {
              closeDialog("cardIdReaderDialog");
              return await onSelect?.(patient, alreadyExists);
            }
            closeDialog("cardIdReaderDialog");
            console.log("patient", patient);

            await openPatientIdentityForm(patient, (newPatient) => {
              onSelect?.(newPatient, false);
            });
          } catch (error) {
            console.error("Error: ", error);
          }
        }}
      />
    ),
  });
}

export function CardIdReader({
  onDetect,
}: {
  onDetect: (nin: string) => void;
}) {
  const webcamRef = useRef<Webcam>(null);
  const [nin, setNin] = useState("");
  const [rawText, setRawText] = useState("");
  const [loading, setLoading] = useState(false);

  const captureAndRecognize = async () => {
    const imageSrc = webcamRef?.current?.getScreenshot();
    if (imageSrc) {
      setLoading(true);
      try {
        const {
          data: { text, confidence },
        } = await Tesseract.recognize(
          imageSrc,
          "eng",
          { logger: (m) => {} } // optional logger for progress
        );

        let newText = text
          .replaceAll(" ", "")
          .replaceAll("S", "5")
          .replaceAll("s", "5")
          .replaceAll("O", "0")
          .replaceAll("o", "0")
          .replace(/\s/g, "");

        console.log("newText", newText);

        let newNin = parseNin(newText) || "";
        if (newNin) {
          beep();
          openToast("success", `Numéro d'identification détecté\n${newNin}`);
          onDetect(newNin);
        }

        setNin(newNin);
        setRawText(newText);
      } catch (error) {
        console.error("OCR Error: ", error);
      }
      setLoading(false);
    }
  };

  function parseNin(text: string) {
    text = text
      .replaceAll(" ", "")
      .replaceAll("S", "5")
      .replaceAll("s", "5")
      .replaceAll("O", "0")
      .replaceAll("o", "0")
      .replace(/\s/g, "");

    const regex = /(?<!\d)(?:10|11)\d{16}(?!\d)/;
    const match = text.match(regex);
    return match ? match[0] : null;
  }

  useEffect(() => {
    let myInterval = setInterval(() => {
      captureAndRecognize();
    }, 500);
    return () => {
      clearInterval(myInterval);
    };
    let timeout = setTimeout(() => {
      let newNin = "109930581001941303";
      beep();
      openToast("success", `Numéro d'identification détecté\n${newNin}`);
      onDetect(newNin);
    }, 1000);
    return () => {
      clearTimeout(timeout);
    };
  }, []);

  let color = "rgba(0,0,0,0.5)";
  return (
    <div className="relative" style={{ textAlign: "center", padding: "20px" }}>
      {/* <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: "100%",
          height: "100%",
          backgroundColor: color,
          mask: "url(#hole-mask)",
          WebkitMask: "url(#hole-mask)",
          zIndex: 1000,
        }}
      >
        <svg width="0" height="0" aria-hidden="true">
          <defs>
            <mask id="hole-mask">
              <rect width="100%" height="100%" fill="white" />
              <rect
                x="140"
                y="190"
                width="100"
                height="100"
                rx="10"
                ry="10"
                fill="black"
              />
            </mask>
          </defs>
        </svg>
      </div> */}
      <Webcam
        audio={false}
        ref={webcamRef}
        screenshotFormat="image/jpeg"
        style={{ width: "800px" }}
      />

      {/* <div className="gray">
        <div className="center gray p20">Veuillez scanner une carte</div>
        <IoCardOutline size={150} color="gray"></IoCardOutline>
        <div className="center gray p20">
          <Loading color="gray"></Loading>
        </div>
      </div> */}
    </div>
  );
}

export function CardIdReaderButton({
  onDetect,
  onSelect,
}: {
  onDetect?: (nin: string) => void;
  onSelect?: (patient: Patient) => void;
}) {
  return (
    <Button
      type="tertiary"
      onClick={async () => {
        openCardIdReaderDialog({
          onDetect: async (nin) => {
            await onDetect?.(nin);
          },
          onSelect: async (patient) => {
            await onSelect?.(patient);
          },
        });
      }}
    >
      Scanner
    </Button>
  );
}

function beep() {
  // Create an audio context and a simple oscillator to generate a beep
  if (typeof window !== "undefined") {
    const audioCtx = new (window.AudioContext ||
      (window as any).webkitAudioContext)();
    const oscillator = audioCtx.createOscillator();
    oscillator.connect(audioCtx.destination);
    oscillator.type = "sine"; // You can choose different waveforms
    oscillator.frequency.setValueAtTime(1000, audioCtx.currentTime);
    oscillator.start();
    oscillator.stop(audioCtx.currentTime + 0.1);
  }
}

async function openPatientIdentityForm(
  patient: Patient,
  onSelect: (patient: Patient) => void
) {
  await openDrawer({
    id: "patientDrawer",
    title: "Patient",
    additionalClasses: ["big", "whiteBody"],
    content: (
      <PatientIdentityForm
        initialValue={patient}
        onChange={(newValue) => {
          patient = newValue;
        }}
      />
    ),
    onConfirm: async (e) => {
      let result = await apiFetch("patients", "addOne", {
        patient,
        serviceId: getServiceId(),
        userId: getUserId(),
      });
      if (!result) throw new Error("Erreur lors de la création du patient");
      onSelect(result);
    },
  });
}
